import { Component, OnInit } from '@angular/core';
import { UntypedFormBuilder, UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import {DataService} from '../../../../Services/data.service';
import { ActivatedRoute, Router } from '@angular/router';
import { HttpErrorResponse } from '@angular/common/http';
import { LocationGroupModel } from '../../../../DataModels/location-group-model';
import { ConfirmDialogService } from '../../../../Services/confirm-dialog.service';
import { BsToastService } from '../../../../Services/bs-toast-service';
import {Title} from '@angular/platform-browser';

@Component({
  selector: 'app-location-group-form',
  templateUrl: './location-group-form.component.html',
  styleUrls: ['./location-group-form.component.css'],
  standalone: false
})
export class LocationGroupFormComponent implements OnInit {
  locationGroupID: string;
  locationGroup: LocationGroupModel;
  locationGroupForm: UntypedFormGroup;
  originalSnapshot: any = {};

  paid = false;
  showDelete = false;
  submitted = false;
  isSaveNew = false;

  buttonConfig: any = {
    save: true,
    close: true,
    reset: true,
    delete: true,
    saveNew: true,
    saveClose: true,
    print: false,
  };

  constructor(
    private table: DataService,
    private notification: BsToastService,
    private formBuilder: UntypedFormBuilder,
    private route: ActivatedRoute,
    private router: Router,
    private confirmDialogService: ConfirmDialogService,
    private titleService: Title
  ) {
    this.createForm();
    this.locationGroupID = this.route.snapshot.params.id;
  }

  get f() {
    return this.locationGroupForm.controls;
  }

  createForm() {
    this.locationGroupForm = this.formBuilder.group({
      name: new UntypedFormControl(''),
      active: new UntypedFormControl(true),
    });
  }

  ngOnInit() {
    this.titleService.setTitle($localize`:Location Group @@locationGroup:Location Group`);
    if (this.locationGroupID) {
      this.LoadData();
      this.buttonConfig.reset = false;
    } else {
      Promise.resolve().then(() => {
        try {
          this.originalSnapshot = this.buildSnapshot();
        } catch {
        }
      });
    }
  }

  private LoadData() {
    this.table.getData('location_Group', this.locationGroupID).subscribe(
      (result) => {
        this.locationGroup = result;
      },
      (error: HttpErrorResponse) => {
        // this.notification.error($localize`:Error loading Location Access @@errorloadingLocationAccess:Error loading Location Access`);
        console.log(error);
      },
      () => {
        this.locationGroupForm.patchValue(this.locationGroup);
        try {
          this.originalSnapshot = this.buildSnapshot();
        } catch {
        }
      }
    );
  }

  resetForm() {
    this.resetToDefaults();
    try {
      this.originalSnapshot = this.buildSnapshot();
    } catch {
    }
  }

  saveNew() {
    // Clear only after async save succeeds
    this.isSaveNew = true;
    this.onSave(false);
  }

  saveClose() {
    if (this.onSave(true)) {
    }
  }

  onClose() {
    this.router.navigate(['admin/location-group-list']).then();
  }

  private resetToDefaults(): void {
    // Reset the form to known defaults and clear component state for a fresh entry
    this.locationGroupForm.reset({
      name: '',
      active: true,
    });
    this.locationGroup = {name: '', active: true} as LocationGroupModel;
    this.locationGroupID = null;
    this.submitted = false;
    try {
      this.originalSnapshot = this.buildSnapshot();
    } catch {
    }
  }

  deleteItem() {
    let retMsg = '';
    const messages: string[] = [
      $localize`:Are you sure you want to delete location group? @@areYouSureYouWantToDeleteLocationGroup:Are you sure you want to delete location group?`,
      this.locationGroup.name,
    ];
    this.confirmDialogService.confirmThis(
      messages,
      () => {
        this.table.deleteDataById('location_Group', this.locationGroupID).subscribe(
          (result) => {
            retMsg = result;
            // this.notification.success($localize`:Deleted @@deleted:Deleted`);
          },
          (error: HttpErrorResponse) => {
            // this.notification.error($localize`:Error @@error:Error`);
            console.log(error);
          },
          () => {
            this.onClose();
          }
        );
      },
      () => {
        this.notification.showSuccessToast($localize`:Delete Canceled @@deleteCanceled:Delete Canceled`);
      }
    );
  }

  onSave(saveClose: boolean) {
    this.submitted = true;
    if (this.locationGroupForm.invalid) {
      this.notification.showWarningToast($localize`:Form Errors! @@formErrors:Form Errors!`);
      return false;
    }
    this.locationGroup = Object.assign({}, this.locationGroupForm.value);
    if (this.locationGroupID) {
      // Update existing
      this.table.patch('location_Group', this.locationGroupID, this.locationGroup).then(
        () => {
          try {
            this.originalSnapshot = this.buildSnapshot();
          } catch {
          }
          if (saveClose) {
            this.onClose();
          } else if (this.isSaveNew) {
            this.isSaveNew = false;
            this.resetToDefaults();
          }
        },
        (error: HttpErrorResponse) => {
          console.log(error);
        }
      ).finally(() => {
        this.submitted = false;
      });
    } else {
      // Create new
      this.table.postWithPromise('location_Group', this.locationGroup).then((res) => {
        // stops double posting when clicking Save
        let apiReturn: any = null;
        if (typeof res === 'object') {
          apiReturn = JSON.parse(JSON.stringify(res));
        }
        this.locationGroupID = apiReturn?.id;
        try {
          this.originalSnapshot = this.buildSnapshot();
        } catch {
        }
        if (saveClose) {
          this.onClose();
        } else if (this.isSaveNew) {
          this.isSaveNew = false;
          this.resetToDefaults();
        }
      }).catch((error: HttpErrorResponse) => {
        console.log(error);
      }).finally(() => {
        this.submitted = false;
      });
    }
    return true;
  }

  buildSnapshot(): any {
    try {
      return this.locationGroupForm.getRawValue();
    } catch {
      return {};
    }
  }

  hasChanges(): boolean {
    try {
      const current = this.buildSnapshot();
      const original = this.originalSnapshot || {};
      return JSON.stringify(current) !== JSON.stringify(original);
    } catch {
      return false;
    }
  }

  checkForChanges(): boolean {
    return this.hasChanges();
  }
}
